import PropTypes from "prop-types";
import React, {useEffect, useMemo, useState} from "react";
import * as Yup from "yup";
import {Form, Formik} from "formik";
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    Typography,
    useMediaQuery
} from "@mui/material";
import { gridSpacing } from "../../../store/constants";
import TextFieldWrapper from "../../../utils/ui-components/FormsUI/TextField";
import SwitchWrapper from "../../../utils/ui-components/FormsUI/Switch";
import { PackageService } from "../../../assets/_services/package-service";
import { openSuccessDialog } from "../../../utils/ui-components/pop-ups/SuccessDialog";
import { SuperAdminService } from "../../../assets/_services/super-admin-service";

CreatePackage.propTypes = {
    fetchData: PropTypes.func,
    handleClose: PropTypes.func.isRequired,
    theme: PropTypes.object.isRequired,
    initialItem: PropTypes.object.isRequired,
};


const FORM_VALIDATION = Yup.object().shape({
    packageName: Yup.string().required("Please enter the package name"),
    numberOfConcurrentCalls: Yup.number().required("Please input this field"),
    numberOfUsers: Yup.number().required("Please input this field"),
    outboundCallWithoutTaxes: Yup.number().required("Please input this field"),
    rentalWithoutTaxes: Yup.number().required("Please input this field"),
    is3CX: Yup.boolean(),
    activeStatus: Yup.boolean(),
});

function CreatePackage({fetchData, handleClose, theme,initialItem}: any) {

    const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));
    const [isLoading, setIsLoading] = useState(false)

    const createPackage = (values: any, setSubmitting: (isSubmitting: boolean) => void) => {
        const formData = {
            packageName : values.packageName,
            numberOfConcurrentCalls : values.numberOfConcurrentCalls,
            numberOfUsers : values.numberOfUsers,
            outboundCallWithoutTaxes : values.outboundCallWithoutTaxes,
            rentalWithoutTaxes : values.rentalWithoutTaxes,
            is3CX : values.is3CX ? 1 : 0,
            activeStatus : values.activeStatus ? 1 : 0
        }

        setIsLoading(true)

        SuperAdminService.createPackage(formData).then(
            response => {
                if(response.isSuccess){
                    openSuccessDialog("Success", response.data.comment)
                    setIsLoading(false)
                    fetchData()
                    handleClose()
                }else{
                    setSubmitting(false)
                    setIsLoading(false)
                }
            }
        )
    };

    const updatePackage = (values: any, setSubmitting: (isSubmitting: boolean) => void) => {
        const formData = {
            packageName : values.packageName,
            packageId : initialItem.packageId,
            numberOfConcurrentCalls : values.numberOfConcurrentCalls,
            numberOfUsers : values.numberOfUsers,
            outboundCallWithoutTaxes : values.outboundCallWithoutTaxes,
            rentalWithoutTaxes : values.rentalWithoutTaxes,
            is3CX : values.is3CX ? 1 : 0,
            activeStatus : values.activeStatus ? 1 : 0
        }

        setIsLoading(true)
        SuperAdminService.updatePackage(formData).then(
            response => {
                if(response.isSuccess){
                    openSuccessDialog("Success", response.data.comment)
                    setIsLoading(false)
                    fetchData()
                    handleClose()
                }else{
                    setSubmitting(false)
                    setIsLoading(false)
                }
            }
        )
    };

    return (
        <Grid container style={{height: '100%'}}>
            <Grid item xs={12}>
                <Formik
                    enableReinitialize={true}
                    initialValues={{
                        ...initialItem
                    }}
                    validationSchema={FORM_VALIDATION}
                    onSubmit={(values: any, {setSubmitting}) => {
                        if(initialItem.packageId === ""){
                            createPackage(values, setSubmitting)
                        }else{
                            updatePackage(values, setSubmitting)
                        }
                    }}
                >
                    {({values, dirty, isSubmitting, isValid, setFieldValue}) => (
                        <Form>
                            <Grid container columnSpacing={1} style={{padding: 20}}>
                                <Grid item lg={12} md={12} sm={12} xs={12} sx={{mb:2}}>
                                    <Typography variant="h3" color="primary">Package Information</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextFieldWrapper
                                        required
                                        type="text"
                                        name="packageName"
                                        size='small'
                                        label="Package Name"
                                    />
                                </Grid>
                                <Grid item lg={6} md={6} sm={6} xs={12}>
                                    <TextFieldWrapper
                                        required
                                        type="number"
                                        name="numberOfConcurrentCalls"
                                        size='small'
                                        label="Concurrent Calls"
                                    />
                                </Grid>
                                <Grid item lg={6} md={6} sm={6} xs={12}>
                                    <TextFieldWrapper
                                        required
                                        type="number"
                                        name="numberOfUsers"
                                        size='small'
                                        label="Users"
                                    />
                                </Grid>
                                <Grid item lg={6} md={6} sm={6} xs={12}>
                                    <TextFieldWrapper
                                        required
                                        type="number"
                                        name="outboundCallWithoutTaxes"
                                        size='small'
                                        label="Outbound Call Without Taxes"
                                    />
                                </Grid>
                                <Grid item lg={6} md={6} sm={6} xs={12}>
                                    <TextFieldWrapper
                                        required
                                        type="number"
                                        name="rentalWithoutTaxes"
                                        size='small'
                                        label="Rental Without Taxes"
                                    />
                                </Grid>
                                <Grid item lg={6} md={6} sm={6} xs={12}>
                                    <SwitchWrapper
                                        color={initialItem.packageId !== "" ? "gray" : values.is3CX ? 'primary' : 'error'}
                                        name="is3CX"
                                        label={values.is3CX ? "3CX Enable" : "3CX Disable"}
                                        disabled={initialItem.packageId !== ""}
                                    />
                                </Grid>
                                <Grid item lg={6} md={6} sm={6} xs={12}>
                                    <SwitchWrapper
                                        color={values.activeStatus ? 'primary' : 'error'}
                                        name="activeStatus"
                                        label={values.activeStatus ? "ACTIVE" : "INACTIVE"}
                                    />
                                </Grid>
                            </Grid>
                            
                            <Box style={{
                                zIndex: 2,
                                width: matchDownSM ? '100vw' : 600,
                                position: 'fixed',
                                bottom: 0,
                                backgroundColor: theme.palette.primary.light
                            }}>
                                <Divider/>
                                <Grid container spacing={gridSpacing} style={{padding: '10px 20px'}}>
                                    <Grid item xs={12} display='flex' justifyContent='end'>
                                        <Button
                                            color="primary" variant="outlined"
                                            onClick={(e: React.KeyboardEvent | React.MouseEvent) => {
                                                handleClose(e)
                                            }}
                                        >
                                            Cancel</Button>
                                        <Box width={10}/>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            type='submit'
                                            startIcon={isLoading && <CircularProgress size={'20px'} sx={{color: '#ffff'}}/>}
                                        >
                                            Save
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Grid>
        </Grid>
    );
}

export default CreatePackage;